import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ArrowLeft, Mail, Save, Loader2, Eye, Code } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../../components/ui/toaster';
import api from '../../services/api';

const EmailTemplateManagement = () => {
    const navigate = useNavigate();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [editSubject, setEditSubject] = useState('');
    const [editBody, setEditBody] = useState('');
    const [saving, setSaving] = useState(false);
    const [editorMode, setEditorMode] = useState('html'); // 'html' | 'preview'

    useEffect(() => {
        fetchTemplates();
    }, []);

    const fetchTemplates = async () => {
        try {
            setLoading(true);
            const data = await api.getNotificationTemplates('email');
            setTemplates(data);
            if (data.length > 0 && !selectedTemplate) {
                selectTemplate(data[0]);
            }
        } catch (error) {
            console.error('Error fetching templates:', error);
            toast({ title: 'Error', description: 'Failed to load email templates', variant: 'destructive' });
        } finally {
            setLoading(false);
        }
    };

    const selectTemplate = (template) => {
        setSelectedTemplate(template);
        setEditSubject(template.templateSubject);
        setEditBody(template.templateBody);
        setEditorMode('html');
    };

    const handleSave = async () => {
        if (!selectedTemplate) return;
        try {
            setSaving(true);
            await api.updateNotificationTemplate(selectedTemplate.id, {
                templateSubject: editSubject,
                templateBody: editBody
            });
            toast({ title: 'Success', description: 'Email template updated successfully' });
            // Update local state
            setTemplates(prev => prev.map(t =>
                t.id === selectedTemplate.id
                    ? { ...t, templateSubject: editSubject, templateBody: editBody }
                    : t
            ));
            setSelectedTemplate(prev => ({ ...prev, templateSubject: editSubject, templateBody: editBody }));
        } catch (error) {
            console.error('Error saving template:', error);
            toast({ title: 'Error', description: 'Failed to update template', variant: 'destructive' });
        } finally {
            setSaving(false);
        }
    };

    const getPreviewHtml = () => {
        if (!selectedTemplate) return '';
        let html = editBody;
        // Replace placeholders with sample values for preview
        const sampleData = {
            actorName: 'John Admin',
            clientName: 'Smith Residence',
            projectId: 'AwPr25260001',
            oemContactPerson: 'Rajesh Kumar',
            siteAddress: '123 MG Road, Hyderabad - 500081',
            projectType: '2 Kitchen(s) + 1 Wardrobe(s)',
            proposedStartDate: '15 May 2026',
        };
        Object.entries(sampleData).forEach(([key, value]) => {
            html = html.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), `<mark style="background:#fef08a;padding:0 2px;">${value}</mark>`);
        });
        // Highlight any remaining placeholders
        html = html.replace(/\{\{(\w+)\}\}/g, '<mark style="background:#fca5a5;padding:0 2px;">{{$1}}</mark>');
        return html;
    };

    const hasChanges = selectedTemplate &&
        (editSubject !== selectedTemplate.templateSubject || editBody !== selectedTemplate.templateBody);

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            <Toaster />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/settings')} className="text-gray-600">
                                <ArrowLeft className="w-4 h-4 mr-1" /> Back to Settings
                            </Button>
                        </div>
                    </div>
                    <div className="flex items-center space-x-3 mt-4">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center shrink-0">
                            <Mail className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                        </div>
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Email Templates</h1>
                            <p className="text-gray-600 text-sm sm:text-base">Manage notification email templates and their content</p>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
                    </div>
                ) : templates.length === 0 ? (
                    <Card>
                        <CardContent className="py-12 text-center">
                            <Mail className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                            <p className="text-gray-500 text-lg">No email templates found</p>
                            <p className="text-gray-400 text-sm mt-1">Email templates will appear here once notification events are configured</p>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="grid grid-cols-12 gap-6">
                        {/* Left sidebar — template list */}
                        <div className="col-span-12 lg:col-span-3">
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                                        Templates ({templates.length})
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="p-0">
                                    <div className="divide-y">
                                        {templates.map((t) => (
                                            <button
                                                key={t.id}
                                                onClick={() => selectTemplate(t)}
                                                className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${selectedTemplate?.id === t.id ? 'bg-teal-50 border-l-4 border-teal-500' : 'border-l-4 border-transparent'
                                                    }`}
                                            >
                                                <p className="font-medium text-sm text-gray-900 truncate">{t.eventTypeName}</p>
                                                <p className="text-xs text-gray-500 mt-0.5">{t.eventTypeKey}</p>
                                            </button>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Placeholders help */}
                            {selectedTemplate?.placeholderKeys?.length > 0 && (
                                <Card className="mt-4">
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                                            Available Placeholders
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="space-y-1.5">
                                            {selectedTemplate.placeholderKeys.map((key) => (
                                                <div key={key} className="flex items-center gap-2">
                                                    <code className="text-xs bg-gray-100 px-2 py-0.5 rounded font-mono text-purple-700">
                                                        {`{{${key}}}`}
                                                    </code>
                                                </div>
                                            ))}
                                        </div>
                                        <p className="text-xs text-gray-400 mt-3">
                                            Use these placeholders in your template. They'll be replaced with actual values when the email is sent.
                                        </p>
                                    </CardContent>
                                </Card>
                            )}
                        </div>

                        {/* Right — editor */}
                        <div className="col-span-12 lg:col-span-9">
                            {selectedTemplate ? (
                                <Card>
                                    <CardHeader>
                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                            <div className="min-w-0">
                                                <CardTitle className="text-lg break-words">{selectedTemplate.eventTypeName}</CardTitle>
                                                <p className="text-sm text-gray-500 mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                                                    <span>Event:</span>
                                                    <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs break-all">{selectedTemplate.eventTypeKey}</code>
                                                    <span className="hidden sm:inline">·</span>
                                                    <span>Channel:</span>
                                                    <code className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded text-xs">{selectedTemplate.channel}</code>
                                                </p>
                                            </div>
                                            <div className="flex flex-wrap items-center gap-2 shrink-0">
                                                <div className="flex rounded-lg border overflow-hidden">
                                                    <button
                                                        onClick={() => setEditorMode('html')}
                                                        className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${editorMode === 'html' ? 'bg-teal-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                                                            }`}
                                                    >
                                                        <Code className="w-3.5 h-3.5" /> HTML
                                                    </button>
                                                    <button
                                                        onClick={() => setEditorMode('preview')}
                                                        className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 border-l transition-colors ${editorMode === 'preview' ? 'bg-teal-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                                                            }`}
                                                    >
                                                        <Eye className="w-3.5 h-3.5" /> Preview
                                                    </button>
                                                </div>
                                                <Button
                                                    size="sm"
                                                    onClick={handleSave}
                                                    disabled={saving || !hasChanges}
                                                    className="bg-teal-600 hover:bg-teal-700"
                                                >
                                                    {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                                                    Save
                                                </Button>
                                            </div>
                                        </div>
                                    </CardHeader>
                                    <CardContent>
                                        {/* Subject */}
                                        <div className="mb-4">
                                            <Label className="text-sm font-medium text-gray-700">Subject Line</Label>
                                            <Input
                                                value={editSubject}
                                                onChange={(e) => setEditSubject(e.target.value)}
                                                className="mt-1"
                                                placeholder="Email subject..."
                                                disabled={editorMode === 'preview'}
                                            />
                                        </div>

                                        {/* Body — editor modes */}
                                        <div className="mb-2">
                                            <Label className="text-sm font-medium text-gray-700">
                                                {editorMode === 'html' ? 'HTML Source' : 'Email Preview'}
                                            </Label>
                                        </div>

                                        {editorMode === 'html' && (
                                            <textarea
                                                value={editBody}
                                                onChange={(e) => setEditBody(e.target.value)}
                                                className="w-full font-mono text-sm border rounded-lg p-4 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                                                style={{ minHeight: '400px', resize: 'vertical' }}
                                                placeholder="HTML email body..."
                                                spellCheck={false}
                                            />
                                        )}

                                        {editorMode === 'preview' && (
                                            <div className="border rounded-lg overflow-hidden">
                                                <div className="bg-gray-50 px-4 py-2 border-b">
                                                    <p className="text-sm text-gray-600">
                                                        <span className="font-medium">Subject:</span> {editSubject.replace(/\{\{(\w+)\}\}/g, '[$1]')}
                                                    </p>
                                                </div>
                                                <iframe
                                                    title="Email Preview"
                                                    srcDoc={getPreviewHtml()}
                                                    className="w-full bg-white border-0"
                                                    style={{ minHeight: '600px', height: '600px' }}
                                                    sandbox="allow-same-origin"
                                                />
                                                <div className="bg-gray-50 px-4 py-2 border-t">
                                                    <p className="text-xs text-gray-400">
                                                        <span className="inline-block w-3 h-3 bg-yellow-200 rounded mr-1 align-middle"></span> Sample values
                                                        <span className="inline-block w-3 h-3 bg-red-200 rounded ml-3 mr-1 align-middle"></span> Unresolved placeholders
                                                    </p>
                                                </div>
                                            </div>
                                        )}

                                        {hasChanges && (
                                            <p className="text-xs text-amber-600 mt-2">
                                                ● You have unsaved changes
                                            </p>
                                        )}
                                    </CardContent>
                                </Card>
                            ) : (
                                <Card>
                                    <CardContent className="py-20 text-center">
                                        <p className="text-gray-400">Select a template from the left to edit</p>
                                    </CardContent>
                                </Card>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EmailTemplateManagement;
